import { createPortal } from "react-dom";

const exact = { WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" };
const qtyText = (n) => (n > 20 ? ">20" : String(n));
const COLORS = { "4.6": "#0f172a", "8.8": "#c2410c" };
const GRADE_NAME = { "4.6": "MS 4.6", "8.8": "HT 8.8" };
const BAG_KG = { "4.6": 50, "8.8": 25 };

export const inchLen = (s) => (s || "").replace(/^(\d+)\.(\d+\/\d+)$/, "$1.$2");

function groupByDia(items) {
  const map = new Map();
  for (const it of items) {
    const k = `${it.system}|${it.dia}`;
    if (!map.has(k)) map.set(k, { system: it.system, dia: it.dia, grade: it.grade, dia_order: it.dia_order, items: [] });
    map.get(k).items.push(it);
  }
  const tOrder = (t) => (t === "HT" ? 1 : 0);
  return [...map.values()]
    .map((g) => ({ ...g, items: g.items.sort((a, b) => tOrder(a.thread) - tOrder(b.thread) || a.len_value - b.len_value || (a.plating ? 1 : 0) - (b.plating ? 1 : 0)) }))
    .sort((a, b) => a.dia_order - b.dia_order);
}

const ROW_H = 13.2;
const HEAD_H = 18;
const BLOCK_GAP = 4;
const COL_H = 940;
const COLS = 5;

function layoutColumns(groups) {
  const cols = [[]];
  let used = 0;
  for (const g of groups) {
    let idx = 0;
    let contd = false;
    while (idx < g.items.length) {
      const remaining = COL_H - used;
      let fit = Math.floor((remaining - HEAD_H - BLOCK_GAP) / ROW_H);
      const left = g.items.length - idx;
      if (fit < Math.min(3, left)) {
        if (cols[cols.length - 1].length) cols.push([]);
        used = 0;
        continue;
      }
      const take = Math.min(fit, left);
      cols[cols.length - 1].push({ ...g, items: g.items.slice(idx, idx + take), contd, prevThread: idx > 0 ? g.items[idx - 1].thread : null });
      used += HEAD_H + BLOCK_GAP + take * ROW_H;
      idx += take;
      contd = true;
    }
  }
  return cols;
}

function ColumnFlow({ groups, color }) {
  const cols = layoutColumns(groups);
  const pages = [];
  for (let i = 0; i < cols.length; i += COLS) pages.push(cols.slice(i, i + COLS));
  return pages.map((pageCols, p) => (
    <div key={p} style={{ display: "flex", gap: "8px", alignItems: "flex-start", ...(p > 0 ? { breakBefore: "page", pageBreakBefore: "always", paddingTop: "6mm" } : {}) }}>
      {Array.from({ length: COLS }).map((_, c) => (
        <div key={c} style={{ flex: 1, minWidth: 0 }}>
          {(pageCols[c] || []).map((b, i) => (
            <DiaBlock key={i} g={b} color={color} />
          ))}
        </div>
      ))}
    </div>
  ));
}

function DiaBlock({ g, color }) {
  const title = `${g.system === "mm" ? "M" : ""}${g.dia}${g.system === "inch" && g.dia === "1" ? '"' : ""}`;
  const cell = { padding: "0.5px 3px", borderBottom: "1px solid #e2e8f0", lineHeight: 1.15 };
  return (
    <div style={{ breakInside: "avoid", pageBreakInside: "avoid", marginBottom: "4px" }}>
      <div style={{ background: color, color: "#fff", fontWeight: 900, fontSize: "10.5px", padding: "1px 5px", borderRadius: "3px 3px 0 0", letterSpacing: "0.04em", display: "flex", justifyContent: "space-between", ...exact }}>
        <span>{title}</span>
        {g.contd ? <span style={{ fontWeight: 400, fontSize: "8px", opacity: 0.85 }}>contd.</span> : null}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10px", tableLayout: "fixed" }}>
        <colgroup>
          <col style={{ width: "18px" }} />
          <col />
          <col style={{ width: "20px" }} />
          <col style={{ width: "46px" }} />
        </colgroup>
        <tbody>
          {g.items.map((it, i) => {
            const prev = i > 0 ? g.items[i - 1].thread : g.prevThread;
            const splitHere = prev && it.thread === "HT" && prev !== "HT";
            return (
              <tr key={i} style={{ background: i % 2 ? "#f1f5f9" : "#fff", ...(splitHere ? { borderTop: "2px solid #94a3b8" } : {}), ...exact }}>
                <td style={{ ...cell, fontWeight: 700, color: "#475569", fontSize: "8.5px" }}>{it.thread}</td>
                <td style={{ ...cell, whiteSpace: "nowrap" }}>
                  {inchLen(it.length)}
                  {it.plating ? <span style={{ fontSize: "8px", fontWeight: 900, color: "#0369a1" }}> ZN</span> : null}
                </td>
                <td style={{ ...cell, fontSize: "7.5px", fontWeight: 700, color: "#64748b", textAlign: "center", padding: "0.5px 1px" }}>{it.grade}</td>
                <td style={{ ...cell, textAlign: "right", fontWeight: 800, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                  {qtyText(it.bags)}
                  <span style={{ fontWeight: 400, color: "#64748b", fontSize: "7.5px" }}> bags</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SectionTitle({ children, sub, color = "#0f172a" }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: "10px", borderBottom: `2px solid ${color}`, margin: "8px 0 5px", paddingBottom: "2px", breakAfter: "avoid" }}>
      <span style={{ fontSize: "13px", fontWeight: 900, letterSpacing: "0.05em", color }}>{children}</span>
      {sub ? <span style={{ fontSize: "9.5px", color: "#475569" }}>{sub}</span> : null}
    </div>
  );
}

function Columns({ children, count = 4 }) {
  return <div style={{ columnCount: count, columnGap: "8px", columnFill: "balance" }}>{children}</div>;
}

function SimpleTable({ rows }) {
  if (!rows.length) return null;
  return (
    <Columns count={4}>
      {rows.map((it, i) => {
        const showNote = it.note && !it.label.toUpperCase().includes(it.note.toUpperCase());
        return (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: "6px", padding: "1.5px 6px", borderBottom: "1px solid #e2e8f0", background: i % 2 ? "#f1f5f9" : "#fff", fontSize: "10.5px", breakInside: "avoid", ...exact }}>
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {it.label}
              {showNote ? <span style={{ color: "#64748b" }}> {it.note}</span> : null}
              {it.plating ? <span style={{ color: "#64748b" }}> · {it.plating}</span> : null}
            </span>
            <span style={{ fontWeight: 800, whiteSpace: "nowrap" }}>
              {qtyText(it.bags)} <span style={{ fontWeight: 400, color: "#64748b", fontSize: "9px" }}>{it.unit || "bags"}</span>
            </span>
          </div>
        );
      })}
    </Columns>
  );
}

function Header({ sheetTitle, date, color, testid }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `2px solid ${color}`, paddingBottom: "4px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <img src="/trb-logo.png" alt="TRB" style={{ height: "40px", width: "40px", objectFit: "contain", ...exact }} />
        <div style={{ fontSize: "9.5px", lineHeight: 1.3, color: "#334155" }}>
          <div>Ph: +91 98780-91782 Ishan Gupta &nbsp;|&nbsp; +91 98728-68722 Naveen Gupta</div>
          <div>Industrial Area C &amp; New Cycle Market, Gill Road, LUDHIANA 141003</div>
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: "20px", fontWeight: 900, letterSpacing: "0.04em", lineHeight: 1.05 }}>TR STOCK LIST</div>
        <div style={{ fontSize: "12.5px", fontWeight: 900, marginTop: "1px", color, ...exact }} data-testid={testid}>
          {sheetTitle}
          {date ? <span style={{ fontWeight: 400, color: "#000", fontSize: "10.5px" }}> &nbsp;·&nbsp; Stock as on <b>{date}</b></span> : null}
        </div>
      </div>
    </div>
  );
}

function Legend({ grade, right, system }) {
  const lengths = system === "inch" ? "inches" : system === "mm" ? "mm" : "inches / mm";
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "9.5px", color: "#334155", margin: "5px 0 2px" }}>
      <span>
        Qty in <b>bags</b> (1 bag = {BAG_KG[grade]} kg) · <b>&gt;20</b> = more than 20 bags · <b>FT</b> full thread listed first, then <b>HT</b> half thread · <b>ZN</b> = zinc plated · lengths in {lengths}
      </span>
      <span>{right}</span>
    </div>
  );
}

function Footer() {
  return (
    <div style={{ marginTop: "8px", fontSize: "9.5px", color: "#475569", borderTop: "1px solid #cbd5e1", paddingTop: "3px" }}>
      Stock position is indicative and subject to prior sale. Sizes not listed are currently out of stock or available on order.
    </div>
  );
}

function BoltPage({ grade, bolts, date, first, title, system }) {
  const color = COLORS[grade];
  const inch = groupByDia(bolts.filter((it) => it.system === "inch"));
  const metric = groupByDia(bolts.filter((it) => it.system === "mm"));
  const name = GRADE_NAME[grade];
  return (
    <div style={{ padding: "6mm 7mm", ...(first ? {} : { breakBefore: "page", pageBreakBefore: "always" }) }} data-testid={`stock-print-sheet-${grade}-${system || "all"}`}>
      <Header sheetTitle={title} date={date} color={color} testid={`stock-print-title-${grade}-${system || "all"}`} />
      <Legend grade={grade} system={system} right={`${bolts.length} ${name} bolt sizes in stock`} />
      <ColumnFlow groups={system === "inch" ? inch : system === "mm" ? metric : [...inch, ...metric]} color={color} />
      <Footer />
    </div>
  );
}

function OthersBlock({ grade, items }) {
  const color = COLORS[grade];
  const name = GRADE_NAME[grade];
  const nuts = items.filter((it) => it.type === "nut").sort((a, b) => a.dia_order - b.dia_order);
  const washers = items.filter((it) => it.type === "washer");
  const others = items.filter((it) => !["nut", "washer"].includes(it.type));
  if (!items.length) return null;
  return (
    <div data-testid={`stock-print-others-${grade}`}>
      <div style={{ background: color, color: "#fff", fontWeight: 900, fontSize: "12px", padding: "3px 8px", marginTop: "10px", borderRadius: "4px", letterSpacing: "0.05em", ...exact }}>
        {name} — NUT · WASHER · THREAD ROD & OTHER ITEMS <span style={{ fontWeight: 400, fontSize: "9.5px" }}>&nbsp; 1 bag = {BAG_KG[grade]} kg</span>
      </div>
      {nuts.length > 0 && (
        <>
          <SectionTitle color={color}>NUTS</SectionTitle>
          <SimpleTable rows={nuts} />
        </>
      )}
      {washers.length > 0 && (
        <>
          <SectionTitle color={color}>WASHERS</SectionTitle>
          <SimpleTable rows={washers} />
        </>
      )}
      {others.length > 0 && (
        <>
          <SectionTitle color={color}>THREAD ROD & OTHER ITEMS</SectionTitle>
          <SimpleTable rows={others} />
        </>
      )}
    </div>
  );
}

export function StockSheet({ grade, asOn, items }) {
  const live = (items || []).filter((it) => Number(it.bags) > 0).map((it) => ({ ...it, grade: it.grade || grade }));
  const d = asOn ? new Date(asOn).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";
  const bolts46 = live.filter((it) => it.grade === "4.6" && it.type === "bolt");
  const bolts88 = live.filter((it) => it.grade === "8.8" && it.type === "bolt");
  const pages = [
    { key: "46-inch", grade: "4.6", system: "inch", title: "MS 4.6 HEX BOLT — BSW (INCHES)", bolts: bolts46.filter((it) => it.system === "inch") },
    { key: "46-mm", grade: "4.6", system: "mm", title: "MS 4.6 HEX BOLT — MM (METRIC)", bolts: bolts46.filter((it) => it.system === "mm") },
    { key: "88", grade: "8.8", system: "", title: "HIGH TENSILE 8.8 HEX BOLT", bolts: bolts88 },
  ].filter((p) => p.bolts.length);
  const othersAll = ["4.6", "8.8"].map((g) => ({ grade: g, others: live.filter((it) => it.grade === g && it.type !== "bolt") })).filter((s) => s.others.length);
  let first = true;

  return (
    <div style={{ fontFamily: "Arial, Helvetica, sans-serif", color: "#000" }}>
      {pages.map((p) => {
        const el = <BoltPage key={p.key} grade={p.grade} system={p.system} title={p.title} bolts={p.bolts} date={d} first={first} />;
        first = false;
        return el;
      })}
      {othersAll.length > 0 && (
        <div style={{ padding: "6mm 7mm", ...(first ? {} : { breakBefore: "page", pageBreakBefore: "always" }) }} data-testid="stock-print-others-sheet">
          <Header sheetTitle="NUT · WASHER · THREAD ROD & OTHER ITEMS" date={d} color="#0f172a" testid="stock-print-others-title" />
          <div style={{ fontSize: "9.5px", color: "#334155", margin: "5px 0 2px" }}>
            Qty in <b>bags</b> unless marked · <b>&gt;20</b> = more than 20 bags · grouped by grade
          </div>
          {othersAll.map((s) => (
            <OthersBlock key={s.grade} grade={s.grade} items={s.others} />
          ))}
          <Footer />
        </div>
      )}
    </div>
  );
}

export function StockPrint({ grade, asOn, items }) {
  if (!items) return null;
  return createPortal(
    <div id="print-area">
      <StockSheet grade={grade} asOn={asOn} items={items} />
    </div>,
    document.body
  );
}
